import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useDeleteLcDocument, useLcDocuments } from "hooks/useLcDocuments"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

import LcDocumentAddEdit from "./LcDocumentAddEdit"

const LcDocumentList = () => {
  document.title = "LC Documents"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useLcDocuments(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteLcDocument()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "LC document deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      toast.error("Failed to delete LC document.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Default",
        cell: ({ row }) =>
          row.original.is_default ? (
            <Badge color="info">Default</Badge>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        header: "Required",
        cell: ({ row }) =>
          row.original.is_required ? (
            <Badge color="warning">Required</Badge>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        header: "Active",
        cell: ({ row }) => (
          <Badge color={row.original.is_active ? "success" : "secondary"}>
            {row.original.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        header: "Sort",
        accessorKey: "sort_order",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <CanView access={[ACCESS.LC_DOCUMENT.EDIT]}>
              <li>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEditClick(row.original)}
                  id={`edit-lc-document-${row.original.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-lc-document-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.LC_DOCUMENT.DELETE]}>
              <li>
                <Button
                  color="danger"
                  size="sm"
                  disabled={row.original.is_required}
                  onClick={() => handleDeleteClick(row.original)}
                  id={`delete-lc-document-${row.original.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`delete-lc-document-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="LC Documents" breadcrumbItem="LC Documents">
      <div className="d-flex align-items-center">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Total <Badge className="badge-soft-info">{paginationInfo?.total ?? 0}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <CanView access={[ACCESS.LC_DOCUMENT.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>
              Add New
            </Button>
          </CanView>
          <Button color="light" onClick={refetch} className="ms-1">
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && (
            <Button color="danger" onClick={resetParams} className="ms-1">
              Reset Filters
            </Button>
          )}
        </div>
      </div>

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <LcDocumentAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default LcDocumentList
